import React, { useMemo } from 'react';
import Typography from "@mui/material/Typography";
import { Box } from "@mui/system";
import Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import Grid from '@mui/material/Unstable_Grid2';
import CircularProgress from '@mui/material/CircularProgress';
import Alert from '@mui/material/Alert';
import PerformanceReturnsTable from 'components/molecules/PerformanceReturnsTable';

/**
 * Generate chart data from performance data
 * 
 * @param {Array} performanceData - Array of performance data objects with date and value
 * @returns {Array} Formatted data for Highcharts
 */
const generateChartData = (performanceData) => {
  if (!performanceData || !performanceData.length) {
    return [];
  }
  
  // Sort data by date (ascending)
  const sortedData = [...performanceData].sort((a, b) => 
    new Date(a.date) - new Date(b.date)
  );
  
  // Transform data for Highcharts
  return sortedData.map(item => {
    // Convert date string to UTC timestamp
    const date = new Date(item.date);
    const timestamp = Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    );
    
    // Convert value to number with 2 decimal places
    const value = parseFloat((parseFloat(item.value) / 1000).toFixed(2));
    
    // Return [timestamp, value] pair for Highcharts
    return [timestamp, value]; // Value already converted to thousands (k) with 2 decimal places
  });
};

/**
 * Calculate range for y-axis based on chart data
 * 
 * @param {Array} chartData - Formatted chart data
 * @returns {Object} Min and max values with padding
 */
const calculateYAxisRange = (chartData) => {
  if (!chartData || chartData.length === 0) {
    return { min: 0, max: 100 };
  }
  
  // Extract values from data
  const values = chartData.map(point => point[1]);
  
  // Get min and max
  const min = Math.min(...values);
  const max = Math.max(...values);
  
  // Add padding (10%)
  const padding = (max - min) * 0.1;
  
  return {
    min: Math.max(0, min - padding), // Don't go below zero
    max: max + padding
  };
};

/**
 * HypotheticalPerformance - Component to display performance chart and monthly returns
 * 
 * @param {Object} props
 * @param {string} props.strategyName - Name of the strategy
 * @param {Array} props.performanceData - Array of performance data objects
 * @param {Object} [props.metrics] - Strategy performance metrics
 * @param {boolean} [props.loading] - Whether data is loading
 * @returns {JSX.Element}
 */
const HypotheticalPerformance = ({ 
  strategyName, 
  performanceData,
  metrics,
  loading = false
}) => {
  // Memoize the chart data
  const chartData = useMemo(() => 
    generateChartData(performanceData), 
    [performanceData]
  );
  
  // Calculate y-axis range
  const yAxisRange = useMemo(() => 
    calculateYAxisRange(chartData), 
    [chartData]
  );
  
  // Generate chart options
  const chartOptions = useMemo(() => ({
    accessibility: {
      enabled: false
    },
    chart: {
      zoomType: 'x',
      backgroundColor: '#F2F2F2',
      style: {
        fontSize: '14px'
      }
    },
    title: {
      text: `${strategyName}`,
      align: 'left',
      style: {
        fontSize: '16px',
        fontWeight: 'bold'
      }
    },
    subtitle: {
      text: document.ontouchstart === undefined ?
        'Click and drag in the plot area to zoom in' : 'Pinch the chart to zoom in',
      align: 'left',
      style: {
        fontSize: '14px'
      }
    },
    xAxis: {
      type: 'datetime',
      labels: {
        format: "{value:%b'%y}",
        style: {
          fontSize: '14px'
        }
      },
      ordinal: false
    },
    yAxis: {
      title: {
        text: '',
        style: {
          fontSize: '14px'
        }
      },
      labels: {
        formatter: function() {
          // Format with a maximum of 2 decimal places to prevent floating point issues
          return '$' + parseFloat(this.value).toFixed(2) + 'k';
        },
        style: {
          fontSize: '14px'
        }
      },
      min: yAxisRange.min,
      max: yAxisRange.max,
      gridLineWidth: 0,
      tickInterval: (yAxisRange.max - yAxisRange.min) / 4
    },
    colors: ["#16171a"],
    legend: {
      enabled: false
    },
    plotOptions: {
      area: {
        fillColor: {
          linearGradient: {
            x1: 0,
            y1: 0,
            x2: 0,
            y2: 2
          },
          stops: [
            [0, "#E6E6E6"],
          ]
        },
        marker: {
          radius: 2,
          enabled: false,
          symbol: 'circle',
          states: {
            hover: {
              enabled: true
            }
          }
        },
        lineWidth: 1.5,
        states: {
          hover: {
            lineWidth: 2
          }
        },
        threshold: null,
        connectNulls: true,
        step: false
      }
    },
    tooltip: {
      headerFormat: '',
      pointFormat: `${strategyName}:<b> ${'$'}{point.y:,.2f}k</b><br/><span style="font-size: 11px; color: #666666">{point.x:%A, %B %e, %Y}</span>`,
      useHTML: true,
      style: {
        fontSize: '14px'
      }
    },
    series: [{
      type: 'area',
      name: 'Account Value',
      data: chartData,
      gapSize: 1,
      gapUnit: 'day'
    }]
  }), [strategyName, chartData, yAxisRange]);

  // If loading, show spinner
  if (loading) {
    return (
      <Grid md={12} xs={12} sx={{ textAlign: 'center', py: 5 }}>
        <CircularProgress />
      </Grid>
    );
  }
  
  // If no data, show message
  if (!performanceData || performanceData.length === 0) {
    return (
      <Grid md={12} xs={12} sx={{ py: 3 }}>
        <Alert severity="info">
          No performance data available for this strategy.
        </Alert>
      </Grid>
    );
  }

  return (
    <Grid md={12} xs={12} order={{ xs: 2, md: 1 }}>
      {/* Monthly Returns Table */}
      <PerformanceReturnsTable 
        performanceData={performanceData}
        title="Monthly Returns"
        minCapital={metrics?.min_capital ? parseFloat(metrics.min_capital) : null}
        loading={loading}
      />

      {/* Performance Chart */}
      <Typography variant="h6" sx={{ mt: 3 }}>Performance</Typography>
      <Typography color={"secondary.dark"} fontWeight={500}>
        {metrics?.annual_return 
          ? `Annual Return: ${metrics.annual_return.toFixed(1)}%` 
          : 'Strategy Performance'
        }
      </Typography>
      <Box sx={{ mt: 3}}>
        <HighchartsReact
          highcharts={Highcharts}
          options={chartOptions}
        />
      </Box>
    </Grid>
  );
};

export default HypotheticalPerformance;
